/**
 * The limited markdown a PO template's text blocks may use (z8nrz7f7dz):
 * paragraphs, line breaks, **bold**, *italic*, `#` / `##` / `###` headings
 * (the built-in terms use all three), and `-` / `1.` lists. Nothing else — no
 * links, images or raw HTML.
 *
 * Parsed to a plain data tree here (pure, tested) and turned into React
 * elements by PoMarkdown. The text is never handed to the DOM as markup, so a
 * template cannot inject anything: `<script>` is just eight characters.
 */

export interface MdSpan {
  text: string
  bold?: boolean
  italic?: boolean
  /** A hard line break before this span (a single newline in a paragraph). */
  br?: boolean
}

export type MdBlock =
  | { type: 'h1' | 'h2' | 'h3' | 'p'; spans: MdSpan[] }
  | { type: 'ul' | 'ol'; items: MdSpan[][] }

/** `**bold**` and `*italic*`, in that order of precedence. Unclosed markers stay literal. */
export function parseInline(text: string): MdSpan[] {
  const spans: MdSpan[] = []
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/g
  let last = 0
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) spans.push({ text: text.slice(last, m.index) })
    if (m[1] !== undefined) spans.push({ text: m[1], bold: true })
    else spans.push({ text: m[2], italic: true })
    last = m.index + m[0].length
  }
  if (last < text.length) spans.push({ text: text.slice(last) })
  return spans
}

/** Inline spans for several lines joined by hard breaks. */
function linesToSpans(lines: string[]): MdSpan[] {
  return lines.flatMap((line, i) => {
    const spans = parseInline(line)
    if (i > 0) {
      if (spans.length === 0) return [{ text: '', br: true }]
      spans[0] = { ...spans[0], br: true }
    }
    return spans
  })
}

const UL = /^\s*[-*]\s+(.*)$/
const OL = /^\s*\d+[.)]\s+(.*)$/

export function parseMarkdown(source: string | null | undefined): MdBlock[] {
  if (!source) return []
  const blocks: MdBlock[] = []
  let para: string[] = []
  let list: { type: 'ul' | 'ol'; items: MdSpan[][] } | null = null

  const flushPara = () => {
    if (para.length) blocks.push({ type: 'p', spans: linesToSpans(para) })
    para = []
  }
  const flushList = () => {
    if (list) blocks.push(list)
    list = null
  }

  for (const raw of source.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd()
    if (line.trim() === '') {
      flushPara()
      flushList()
      continue
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(line)
    if (heading) {
      flushPara()
      flushList()
      const type = (['h1', 'h2', 'h3'] as const)[heading[1].length - 1]
      blocks.push({ type, spans: parseInline(heading[2]) })
      continue
    }
    const ul = UL.exec(line)
    const ol = ul ? null : OL.exec(line)
    if (ul || ol) {
      flushPara()
      const type = ul ? 'ul' : 'ol'
      if (list && list.type !== type) flushList()
      if (!list) list = { type, items: [] }
      list.items.push(parseInline((ul ?? ol)![1]))
      continue
    }
    flushList()
    para.push(line)
  }
  flushPara()
  flushList()
  return blocks
}
